import type { AxiosResponse } from "axios"
import { clean_params, parse_unix_date } from "@/core/helpers/utils"
import type { User, UserRole } from '@/types/user'
import type { TeamCompact, TeamFull, TeamInvite, PublicTeamJoinInfo } from '@/types/team'


export type RegisterToTeamPayload = {
  invite_key: string
  email_address: string
  name: string
  password: string
  terms_accepted: boolean
}

export type RegisterToTeamResult = {
  id: number | null
  team_id: number | null
}

export type RegisterTeamPayload = {
  invite_key?: string
  name: string
  email: string
  password: string
  terms_accepted: boolean
  team_name?: string
  cf_turnstile_token?: string
}

export type RegisterTeamResult = {
  email_verification_needed: boolean
}

export type UpdateTeamPayload = {
  name?: string
  url?: string
  logo_url?: string
}

export type UpdateUserPayload = {
  name?: string | null
  job_title?: string | null
  avatar_url?: string
}

// Text profile fields only; the avatar has its own request (update_my_avatar)
export type UpdateProfilePayload = Omit<UpdateUserPayload, "avatar_url">

type UserResponse = Omit<User, "created" | "is_admin"> & {
  created: unknown
  is_admin?: boolean
  roles?: UserRole[]
}

type TeamInviteResponse = Omit<TeamInvite, "created" | "is_admin"> & {
  created: unknown
  is_admin?: boolean
  roles?: UserRole[]
}

export type UserInviteDetails = {
  // Host team name
  team_name: string
  // Host team logo
  team_logo: string | null
  // Email of the invited user (readonly, they must join with this email)
  email_address: string
  // Name of the invited user (may not be set)
  name: string | null
  // Whether the user will be an admin after joining
  is_admin: boolean

}

/**
 * @description Service to call User Service HTTP requests via Axios.
 */
class UserService {
    /**
     * @description Service base URL (without trailing `/`)
     */
    public static base_url: string = import.meta.env.VITE_APP_USER_SERVICE_URL

    public static axios: any

    public static init (axiosInstance: any) {
      UserService.axios = axiosInstance
    }

    private static parse_user(user: UserResponse): User {
      const { roles, is_admin, created, ...user_data } = user

      return {
        ...user_data,
        created: parse_unix_date(created),
        is_admin: roles ? roles.includes("OA") : Boolean(is_admin)
      }
    }

    private static parse_company_dates<T extends TeamCompact | TeamFull>(company: T): T {
      company.created = parse_unix_date(company.created)
      if (company.plan_expires !== null) {
        company.plan_expires = parse_unix_date(company.plan_expires)
      }
      return company
    }

    private static parse_company_invite(invite: TeamInviteResponse): TeamInvite {
      const { roles, is_admin, created, ...invite_data } = invite

      return {
        ...invite_data,
        is_admin: roles ? roles.includes("OA") : Boolean(is_admin),
        created: parse_unix_date(created)
      }
    }

    public static list_company_users(mark_admins: boolean = false): Promise<AxiosResponse<User[]>> {
      // Loads all members of the user's team
      const params = clean_params({
        mark_admins: Number(mark_admins)
      })

      return UserService.axios.get(`${UserService.base_url}/users`, { params })
        .then((response: AxiosResponse<UserResponse[]>) => {
          const parsed_response = response as unknown as AxiosResponse<User[]>
          parsed_response.data = response.data.map(UserService.parse_user)
          return parsed_response
        })
    }


    public static update_user(user_id: number, user: UpdateUserPayload): Promise<AxiosResponse<boolean>> {
      // Team admin updates the profile of another team member
      return UserService.axios.put(`${UserService.base_url}/users/${user_id}`, user)
    }

    public static get_me(): Promise<AxiosResponse<User>> {
      // Loads the currently logged in user profile
      return UserService.axios.get(`${UserService.base_url}/users/me`)
        .then((response: AxiosResponse<UserResponse>) => {
          const parsed_response = response as unknown as AxiosResponse<User>
          parsed_response.data = UserService.parse_user(response.data)
          return parsed_response
        })
    }

    public static update_me(profile: UpdateProfilePayload): Promise<AxiosResponse<boolean>> {
      // Logged-in user updates their own profile text fields; only the given fields change
      return UserService.axios.put(`${UserService.base_url}/users/me`, profile)
    }

    public static update_my_avatar(avatar_url: string): Promise<AxiosResponse<boolean>> {
      // Logged-in user sets their own avatar: a base64 image (stored by the backend), a URL, or "" to remove it
      return UserService.axios.put(`${UserService.base_url}/users/me`, { avatar_url })
    }

    public static register_to_team(payload: RegisterToTeamPayload): Promise<AxiosResponse<RegisterToTeamResult>> {
      // Public endpoint where a new user joins a team with the join key
      return UserService.axios.post(`${UserService.base_url}/register_to_team`, payload, {
        skipAuthLogoutOn401: true // Disable the 401 interceptor (that redirects to login), so the form can handle 401 (failed capcha) itself without redirection
      })
    }

    public static get_team_info(join_key: string): Promise<AxiosResponse<PublicTeamJoinInfo>> {
      // Public endpoint that loads the public team info for the join form
      return UserService.axios.get(`${UserService.base_url}/company_info`, {
        params: { key: join_key }
      })
    }

    public static register_new_team(payload: RegisterTeamPayload): Promise<AxiosResponse<RegisterTeamResult>> {
      // Create a new team account, where the embedded user will be the first team admin
      return UserService.axios.post(`${UserService.base_url}/register_team`, payload, {
        skipAuthLogoutOn401: true // Disable the 401 interceptor (that redirects to login), so the form can handle 401 (failed capcha) itself without redirection
      })
    }

    public static get_my_team(): Promise<AxiosResponse<TeamCompact | TeamFull>> {
      // Returns the team details of the logged in user
      return UserService.axios.get(`${UserService.base_url}/companies/me`)
        .then((response: AxiosResponse<TeamCompact | TeamFull>) => {
          UserService.parse_company_dates(response.data)
          return response
        })
    }

    public static update_my_team(payload: UpdateTeamPayload): Promise<AxiosResponse<boolean>> {
      // Update the team settings (only available for team admins)
      return UserService.axios.put(`${UserService.base_url}/companies/me`, payload)
    }

    public static list_team_invited_users(): Promise<AxiosResponse<TeamInvite[]>> {
      // List all invited team members (only available for team admins)
      return UserService.axios.get(`${UserService.base_url}/companies/invites`)
        .then((response: AxiosResponse<TeamInviteResponse[]>) => {
          const parsed_response = response as unknown as AxiosResponse<TeamInvite[]>
          parsed_response.data = response.data.map(UserService.parse_company_invite)
          return parsed_response
        })
    }

    public static add_team_user_email(user_email: string, name: string|null, is_admin: boolean, message: string|null): Promise<AxiosResponse<void>> {
      // Invite a new person to the team (only available for team admins)
      const payload = clean_params({
        user_email,
        name,
        is_admin,
        message
      })

      return UserService.axios.post(`${UserService.base_url}/companies/user_email`, payload)
    }

    public static delete_company_user_email(email: string): Promise<AxiosResponse<void>> {
      const payload = { email }
      return UserService.axios.post(`${UserService.base_url}/companies/delete_user_email`, payload)
    }

    public static get_user_invite(invite_key: string): Promise<AxiosResponse<UserInviteDetails>> {
      return UserService.axios.get(`${UserService.base_url}/invite/user_email`, {
        params: { invite_key },
        skipAuthLogoutOn401: true
      })
    }
}

export default UserService
